import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import React from 'react';

import {
	TableColumnCellWithHoverContext,
	TablePage,
	TableRow,
	TableWrapper,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableElements';
import { useTranslationCapTable } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { flexRender, Table } from '@tanstack/react-table';
import { RowIdentificationKey } from '@dealstack/shared/lib/common/business/cap/capTable.types.ts';
import { Button, Flex } from '@mantine/core';
import { generatePath, Link, useParams } from 'react-router-dom';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState';
import { useCalculateElementMaxHeight } from '@dealstack/frontend/shared/other/react/hooks/useCalculateElementMaxHeight.ts';
import { useCapTableVirtualizer } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/useCapTableVirtualizer.ts';
import { CapTableRow } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/capTableTypes.ts';
import {
	GENERIC_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds.ts';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';

interface Props {
	table: Table<CapTableRow>;
	isLoadingData: boolean;
	isExportingData: boolean;
	isTableEmpty: boolean;
	tablePageRef: React.RefObject<HTMLDivElement>;
	tableRef: React.RefObject<HTMLTableElement>;
	tableWrapperRef: React.RefObject<HTMLDivElement>;
}

export const CapTable = ({
	table,
	isLoadingData,
	isExportingData,
	isTableEmpty,
	tablePageRef,
	tableRef,
	tableWrapperRef,
}: Props) => {
	const tCapTable = useTranslationCapTable();
	const {
		rows,
		rowVirtualizer,
		columnVirtualizer,
		tableBodyRef,
		virtualRowIndexes,
		rowRefsMap,
		virtualColumnIndexes,
		staticColumns,
		tableHeaderRowRef,
	} = useCapTableVirtualizer(table, tableWrapperRef);
	const { dealId } = useParams();

	const { maxHeight = 0 } = useCalculateElementMaxHeight(48, [rows?.length], tablePageRef);

	// to show loader and avoid empty data flickering while parsing and rendering large data sets
	const isRenderingTable =
		!isLoadingData && !isTableEmpty && !tableBodyRef.current?.children?.length;

	// this can move to CapTablePage when in page feature flag is removed
	useBreadcrumbs([
		{
			name: tCapTable('title'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_TABLE}`, {
				dealId: dealId ?? '',
			}),
		},
	]);

	return (
		<TablePage ref={tablePageRef} $showTableShadow={!!virtualRowIndexes.length}>
			{(isLoadingData || isExportingData || isRenderingTable) && <InlineLoader />}
			{!isLoadingData && !isTableEmpty && (
				<TableWrapper
					ref={tableWrapperRef}
					$maxHeight={maxHeight}
					$bodyHeight={rowVirtualizer.getTotalSize()}
				>
					<table ref={tableRef}>
						<thead>
							{table.getHeaderGroups().map((headerGroup) => {
								return (
									<tr key={headerGroup.id} ref={tableHeaderRowRef}>
										<th
											className="left-column-spacer"
											data-html2canvas-ignore
										/>

										{staticColumns.map((_, index) => {
											const header = headerGroup.headers[index];

											return (
												<React.Fragment key={header.id}>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
												</React.Fragment>
											);
										})}

										{virtualColumnIndexes.map((virtualColumnIndex) => {
											const header =
												headerGroup.headers[
													virtualColumnIndex + staticColumns.length
												];

											return (
												<React.Fragment key={header.id}>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
												</React.Fragment>
											);
										})}

										<th
											className="right-column-spacer"
											data-html2canvas-ignore
										/>
									</tr>
								);
							})}
						</thead>
						<tbody ref={tableBodyRef}>
							{virtualRowIndexes.map((virtualRowIndex) => {
								const row = rows[virtualRowIndex];
								const isMipRow =
									RowIdentificationKey.issuersHoldings in row.original;
								const isEntityRow =
									RowIdentificationKey.issuerSummarizedHolders in row.original;

								const visibleCells = row.getVisibleCells();
								const virtualColumnIndexes = columnVirtualizer.getVirtualIndexes();
								const dataTestId =
									'securityName' in row.original
										? makeComplexDataTestId(
												GENERIC_ELEMENT_ID.MANTINE_TABLE_ROW,
												row.original.securityName
											)
										: null;

								return (
									<TableRow
										data-index={virtualRowIndex}
										ref={(node) => {
											if (node) {
												rowRefsMap.current.set(virtualRowIndex, node);
												rowVirtualizer.measureElement(node);
											}
										}}
										key={row.id}
										$isEntityRow={isEntityRow}
										$isMipRow={isMipRow}
										color={row.original.color}
										data-testid={dataTestId}
									>
										<td
											className="left-column-spacer"
											data-html2canvas-ignore
										/>

										{staticColumns.map((_, staticColumnIndex) => {
											const cell = visibleCells[staticColumnIndex];
											return (
												<TableColumnCellWithHoverContext
													key={cell.id}
													cell={cell}
												/>
											);
										})}

										{virtualColumnIndexes.map(
											(virtualColumnIndex, arrayIndex) => {
												const cell =
													visibleCells[
														virtualColumnIndex + staticColumns.length
													];

												// skip static columns + left-column-spacer
												// arrayIndex is important to use proper column and avoid cells shifting
												const columnHeaderCellIndex =
													arrayIndex + staticColumns.length + 1;

												const columnHeaderCell:
													| HTMLTableCellElement
													| undefined = tableHeaderRowRef.current
													?.childNodes[
													columnHeaderCellIndex
												] as HTMLTableCellElement;

												return (
													<TableColumnCellWithHoverContext
														key={cell.id}
														cell={cell}
														width={columnHeaderCell?.clientWidth}
													/>
												);
											}
										)}
										<td
											className="right-column-spacer"
											data-html2canvas-ignore
										/>
									</TableRow>
								);
							})}
						</tbody>
					</table>
				</TableWrapper>
			)}
			{isTableEmpty ? (
				<Flex justify="center" w="100%" h="100%">
					<EmptyState title={tCapTable('empty.message')}>
						<Link to={generatePath(`..${DEAL_ROUTES.PEOPLE}`, { personId: '' })}>
							<Button
								variant="default"
								rightSection={<Icon variant="arrow-right" size="lg" />}
							>
								{tCapTable('empty.btn_label')}
							</Button>
						</Link>
					</EmptyState>
				</Flex>
			) : null}
		</TablePage>
	);
};
